/**
 * /lab の一覧カードに出すインタビューのバナーを、表示幅に近い版で配る。
 *
 * 元のバナーは 1280〜1733px 幅で、カードの枠は 170〜320px しかない。
 * 画像の変換 URL は契約していないので (article-list-thumbnail.ts と同じ)、
 * 横 640px に焼いた版を元と同じ場所に `-640w.webp` の名前で置いてある。
 * 640px あれば 320px の枠を 2 倍密度でも埋められる。
 *
 * 元画像の幅は 1280w と書く。3 枚だけ 1733px あるが、少なめに申告しても
 * 足りないときに元を選ぶ動きは変わらない。多めに申告すると、足りない
 * 画面で小さい版を選んでぼやける
 */
export const LAB_CARD_IMAGE_SMALL_WIDTH = 640;

/*
縮小版 (-640w) を実際に置いてある画像の名前。ここに無い画像には srcSet を付けない。
画像を足したら縮小版も焼いてここへ足す (lab-card-image.test.ts が public/lab と突き合わせ、ずれると落ちる)
*/
export const LAB_IMAGES_WITH_SMALL: ReadonlySet<string> = new Set([
  "5day-challenge-2026-09",
  "ai-era-engineer-worth-it-2026-09",
  "ai-side-income-reality-2026-09",
  "all-plans",
  "career-change-hype-is-over-2026-09",
  "career-guarantee-dark-side",
  "career-guarantee-dark-side-2026-09",
  "community-2026-08-event",
  "community-2026-09-event",
  "engineer-job-types-2026-09",
  "first-cohort-now",
  "first-three-months-who-stays-2026-09",
  "free-programming-school-30days",
  "free-trial-5days",
  "free-trial-7days",
  "interview-okada",
  "interview-rikuto-ikuta",
  "interview-tomoki-obi",
  "interview-ue-mentor",
  "interview-yamamoto",
  "it-skills-pay-off-2026-09",
  "job-diagnosis",
  "job-diagnosis-type-2026-10",
  "learning-roadmap",
  "legendary-appliance-engineer-2026-09",
  "ogp-default-2026-09-24",
  "ogp-fallback-a",
  "ogp-fallback-b",
  "ogp-fallback-c",
  "pc-what-to-buy-2026-09",
  "pickup-job-diagnosis-2026-09-28",
  "pillar-01-career-reality-2026-09",
  "pillar-02-study-reality-2026-09",
  "pillar-03-school-choice-2026-09",
  "remote-work-not-decided-by-code-2026-09",
  "self-taught-engineer-reality-2026-09",
  "sidebar-faq-2026-09-28",
  "sidebar-fortune-2026-09-28",
  "sidebar-typing-2026-09-28",
  "student-interview-01-aine",
  "student-interview-02-tabi",
  "student-interview-03-miyazaki",
  "student-interview-04-yuuki",
  "student-interview-05-asami",
  "student-interview-06-nabe",
  "student-interview-07-freelance",
  "student-interview-08-seki",
  "student-interview-09-yuuki",
  "student-interview-10-potato",
  "student-interview-11-yuri",
  "subsidized-school-reality",
  "subsidized-school-reality-2026-09",
  "three-months-engineer-lowest-bar",
  "three-months-engineer-lowest-bar-2026-09",
  "web-designer-from-zero-reality-2026-09",
  "why-good-engineers-avoid-schools",
  "why-good-engineers-avoid-schools-2026-09",
  "yumesaku-faq-2026-09-29",
  "yumesaku-not-for-you",
  "yumesaku-not-for-you-2026-09",
  "yumesaku-reputation-full",
  "yumesaku-reputation-full-2026-09",
  "yumesaku-typing-for-beginners-2026-09-29",
]);

/*
記事に絵が無いときの共有画像 (lab-ogp.ts) は保管先の jpg と png で、1枚 270〜360KB ある。
画面に出すときだけ、同じ絵を webp に焼き直した版 (public/lab) へ差し替える。
共有用の meta は元の URL のまま
*/
const LAB_IMAGE_REPLACEMENTS: Readonly<Record<string, string>> = {
  "https://storage.chotdekiru.com/ogp/yumesaku_lab_ogp_2026_09_24.jpg":
    "/lab/ogp-default-2026-09-24.webp",
  "https://storage.chotdekiru.com/ogp/yumesaku_lab_ogp_a.png":
    "/lab/ogp-fallback-a.webp",
  "https://storage.chotdekiru.com/ogp/yumesaku_lab_ogp_b.png":
    "/lab/ogp-fallback-b.webp",
  "https://storage.chotdekiru.com/ogp/yumesaku_lab_ogp_c.png":
    "/lab/ogp-fallback-c.webp",
};

const LAB_IMAGE_PATTERN = /^((?:https:\/\/yumesaku\.tech)?\/lab\/)([^/]+)\.webp$/;

export function labCardSmallImageUrl(imageUrl: string): string | null {
  const match = LAB_IMAGE_PATTERN.exec(imageUrl);
  if (!match) return null;
  const [, , name] = match;
  if (name.endsWith(`-${LAB_CARD_IMAGE_SMALL_WIDTH}w`)) return null;
  if (!LAB_IMAGES_WITH_SMALL.has(name)) return null;
  // 絶対 URL で来ても同じ配信元なので、縮小版は相対で返す
  return `/lab/${name}-${LAB_CARD_IMAGE_SMALL_WIDTH}w.webp`;
}

export function labCardImageSrcSet(imageUrl: string): string | undefined {
  const small = labCardSmallImageUrl(imageUrl);
  if (!small) return undefined;
  return `${small} ${LAB_CARD_IMAGE_SMALL_WIDTH}w, ${imageUrl} 1280w`;
}

/**
 * img に渡す src と srcSet。縮小版が無い画像 (外部の保管先など) は src だけを返す。
 * sizes は srcSet があるときだけ付ける
 */
export function labCardImageProps(rawImageUrl: string, sizes: string) {
  const imageUrl = LAB_IMAGE_REPLACEMENTS[rawImageUrl] ?? rawImageUrl;
  const srcSet = labCardImageSrcSet(imageUrl);
  return srcSet
    ? { src: imageUrl, srcSet, sizes }
    : { src: imageUrl };
}
